import { useMemo } from 'react'
import { useNavigate } from 'react-router'

import { FilaDetalle, HojaInferior } from '@/components/HojaInferior'
import { Money } from '@/components/Money'
import { ProgressRing } from '@/components/Donut'
import type { EstadoDinero, SeleccionMapa } from '@/components/MapaDelDinero'
import type { PoolBreakdown } from '@paybae/shared'
import type { Transaction } from '@/lib/api'
import { useAccounts, useCards, useGoals, useTransactions } from '@/lib/queries'
import { personPalette } from '@/lib/persons'

/**
 * El desglose detrás de cada tramo del mapa.
 *
 * Una gráfica que no se puede abrir obliga a creer en su número. Aquí cada
 * tramo responde de dónde sale: el libre se descompone en cómo se llega a esa
 * cifra, el ahorro en los fondos que lo tienen apartado, y el gastado en los
 * movimientos que lo consumieron, con quién lo hizo y con qué pagó.
 *
 * El desglose del libre **empieza por la aritmética y luego enseña las
 * cuentas**. Al revés no cuadraba: listar solo los saldos dejaba ver $9,400
 * bajo un titular de $22,750, porque el disponible también cuenta las
 * aportaciones que faltan por depositar y descuenta el ahorro comprometido. Un
 * desglose que no suma el número que encabeza no es un desglose, es una
 * contradicción.
 *
 * Por eso la nómina que falta por caer va **después** del total y no dentro de
 * la suma: no es dinero libre de hoy. La aritmética cierra en «Libre hoy» —el
 * mismo número del titular— y solo entonces se da el paso a la proyección.
 *
 * Todo sale de datos que la app ya pide para otras pantallas, así que abrir el
 * detalle no dispara una consulta nueva: se siente instantáneo porque lo es.
 */

const TITULO: Record<EstadoDinero, string> = {
  libre: 'Dinero libre',
  ahorrado: 'Dinero ahorrado',
  gastado: 'Dinero gastado',
}

export function DetalleDinero({
  seleccion,
  nombreTitular,
  bolsa,
  periodoDesde,
  periodoHasta,
  onCerrar,
}: {
  seleccion: SeleccionMapa | null
  /** Nombre de la persona o del fondo, para encabezar la hoja. */
  nombreTitular: string
  /** La bolsa del titular, tal como la calculó el motor. De aquí sale la suma. */
  bolsa: PoolBreakdown | null
  periodoDesde: string
  periodoHasta: string
  onCerrar: () => void
}) {
  const estado = seleccion?.estado ?? 'libre'

  return (
    <HojaInferior
      abierta={seleccion !== null}
      titulo={`${TITULO[estado]} · ${nombreTitular}`}
      descripcion={descripcionDe(estado)}
      onCerrar={onCerrar}
    >
      {seleccion === null ? null : estado === 'libre' ? (
        <DondeEsta holderId={seleccion.holderId} bolsa={bolsa} />
      ) : estado === 'ahorrado' ? (
        <EnQueEstaGuardado holderId={seleccion.holderId} />
      ) : (
        <EnQueSeFue
          holderId={seleccion.holderId}
          desde={periodoDesde}
          hasta={periodoHasta}
          onCerrar={onCerrar}
        />
      )}
    </HojaInferior>
  )
}

function descripcionDe(estado: EstadoDinero): string {
  if (estado === 'libre') return 'Cómo se llega a esa cifra y dónde está el dinero.'
  if (estado === 'ahorrado') return 'A qué fondo pertenece y cuánto le falta.'
  return 'En qué se fue, quién lo gastó y con qué se pagó.'
}

/**
 * §18 — cómo se llega al libre, y dónde está el dinero.
 *
 * Primero la cuenta, después las cuentas. El disponible no es el saldo: suma
 * lo que está por entrar y resta lo que ya tiene destino, así que enseñar solo
 * los saldos deja al titular sin explicación.
 */
function DondeEsta({ holderId, bolsa }: { holderId: string | null; bolsa: PoolBreakdown | null }) {
  const { data: cuentas, isPending } = useAccounts()

  const propias = useMemo(
    () =>
      (cuentas ?? []).filter(
        (c) =>
          c.ownerMemberId === holderId &&
          c.includeInAvailable &&
          c.type !== 'SAVINGS' &&
          c.type !== 'INVESTMENT' &&
          c.type !== 'MEAL_VOUCHER' &&
          c.archivedAt === null,
      ),
    [cuentas, holderId],
  )

  const vales = useMemo(
    () =>
      (cuentas ?? []).filter(
        (c) => c.ownerMemberId === holderId && c.type === 'MEAL_VOUCHER' && c.archivedAt === null,
      ),
    [cuentas, holderId],
  )

  if (isPending) return <Cargando />
  if (!bolsa && propias.length === 0 && vales.length === 0) {
    return <Vacio texto="Todavía no hay cuentas registradas aquí. Agrega una desde Ajustes." />
  }

  // Solo los renglones con monto: una lista con seis ceros esconde los dos
  // números que sí explican algo.
  // «Ingresos por llegar» ya no está entre estos renglones: no forma parte de lo
  // libre de hoy, así que sumarlo aquí descuadraría el total. Va después, en su
  // propio paso hacia la proyección.
  const renglones: Array<{ etiqueta: string; cents: number; resta?: boolean }> = bolsa
    ? [
        { etiqueta: 'En cuentas hoy', cents: bolsa.liquidCents },
        { etiqueta: 'Aportaciones por recibir', cents: bolsa.incomingContributionsCents },
        { etiqueta: 'Gastos fijos pendientes', cents: bolsa.committedFixedCents, resta: true },
        { etiqueta: 'Tarjetas que vencen', cents: bolsa.committedCardsCents, resta: true },
        { etiqueta: 'Ahorro comprometido', cents: bolsa.committedSavingsCents, resta: true },
        { etiqueta: 'Tu aportación pendiente', cents: bolsa.contributionPendingCents, resta: true },
      ].filter((r) => r.cents !== 0)
    : []

  return (
    <>
      {bolsa ? (
        <>
          <Encabezado>Cómo se calcula</Encabezado>
          {renglones.map((r) => (
            <FilaDetalle
              key={r.etiqueta}
              titulo={r.etiqueta}
              monto={
                <span className="flex items-center gap-1">
                  <span className="font-mono text-[13px] text-ink-faint">{r.resta ? '−' : '+'}</span>
                  <Money cents={r.cents} tone={r.resta ? 'soft' : 'ink'} />
                </span>
              }
            />
          ))}
          <div className="mt-1 flex items-center gap-3 rounded-card bg-primary-soft px-3.5 py-3">
            <span className="flex-1 text-[14px] font-semibold text-ink">Libre hoy</span>
            <Money cents={bolsa.freeCents} className="text-[16px] font-bold" />
          </div>

          {/* La nómina que falta va después del total, no dentro de la suma.
              Es un segundo paso —«y si además llega lo que esperas…»— y por eso
              se lee como una proyección y no como saldo. Mezclarla arriba es lo
              que hacía que el titular prometiera dinero que no había llegado. */}
          {bolsa.expectedIncomeCents !== 0 ? (
            <>
              <FilaDetalle
                titulo="Nómina que falta por caer"
                monto={
                  <span className="flex items-center gap-1">
                    <span className="font-mono text-[13px] text-ink-faint">+</span>
                    <Money cents={bolsa.expectedIncomeCents} tone="income" />
                  </span>
                }
              />
              <div className="mt-1 flex items-center gap-3 rounded-card bg-surface-sunken px-3.5 py-3">
                <span className="flex-1 text-[14px] font-semibold text-ink-soft">
                  Si todo llega, al cierre del periodo
                </span>
                <Money cents={bolsa.projectedFreeCents} className="text-[15px] font-bold" />
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {propias.length > 0 ? (
        <>
          <Encabezado>Dónde está guardado</Encabezado>
          {propias.map((c) => (
            <FilaDetalle
              key={c.id}
              titulo={c.name}
              detalle={c.institution ?? nombreTipo(c.type)}
              monto={<Money cents={c.balanceCents} />}
            />
          ))}
        </>
      ) : null}

      {vales.length > 0 ? (
        <>
          <Encabezado>Vales de despensa</Encabezado>
          {vales.map((c) => (
            <FilaDetalle
              key={c.id}
              titulo={c.name}
              detalle="Solo despensa — no entra en el cálculo de arriba"
              monto={<Money cents={c.balanceCents} tone="soft" />}
            />
          ))}
        </>
      ) : null}
    </>
  )
}

/** §19 — el ahorro, repartido entre fondos, con lo que le falta a cada uno. */
function EnQueEstaGuardado({ holderId }: { holderId: string | null }) {
  const { data: metas, isPending } = useGoals()
  const navigate = useNavigate()

  const propias = useMemo(
    () => (metas ?? []).filter((g) => g.ownerMemberId === holderId),
    [metas, holderId],
  )

  if (isPending) return <Cargando />
  if (propias.length === 0) return <Vacio texto="Todavía no hay fondos ni metas aquí." />

  return (
    <>
      {propias.map((g) => {
        const paleta = personPalette(g.colorToken)
        const pct = g.progress?.percent ?? 0
        return (
          <button
            key={g.id}
            type="button"
            onClick={() => navigate(`/ahorro/${g.id}/editar`)}
            className="flex min-h-[64px] w-full items-center gap-3 rounded-card bg-surface px-3.5 py-2.5 text-left active:scale-[0.99]"
          >
            <ProgressRing percent={pct} size={38} stroke={4} color={paleta.cssVar}>
              <span className="font-mono text-[9px] font-semibold text-ink-soft">
                {Math.round(pct)}
              </span>
            </ProgressRing>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-medium text-ink">{g.name}</span>
              <span className="block truncate text-[12px] text-ink-soft">
                {/* Un fondo sin meta no tiene «cuánto falta»: tiene un saldo.
                    Mostrarle un porcentaje sería inventarle un final. */}
                {g.targetAmountCents == null
                  ? 'Sin meta fija'
                  : `Faltan ${formatoCorto(g.progress?.remainingCents ?? 0)}`}
              </span>
            </span>
            <Money cents={g.currentAmountCents} className="shrink-0" />
          </button>
        )
      })}
    </>
  )
}

/** §20 — el gastado, con quién y con qué. Y la deuda de tarjetas aparte. */
function EnQueSeFue({
  holderId,
  desde,
  hasta,
  onCerrar,
}: {
  holderId: string | null
  desde: string
  hasta: string
  onCerrar: () => void
}) {
  const navigate = useNavigate()
  // El filtro va al servidor, no a la lista ya traída: filtrar aquí dejaría
  // fuera los gastos que no cupieron en la página, y además el servidor es el
  // que sabe qué tiene permitido ver quien pregunta.
  const { data: pagina, isPending } = useTransactions({
    from: desde as never,
    to: hasta as never,
    memberId: holderId ?? 'joint',
    type: 'EXPENSE',
    pageSize: 50,
  })
  const { data: tarjetas } = useCards()

  const movimientos = pagina?.items ?? []

  const deudas = useMemo(
    () => (tarjetas ?? []).filter((c) => c.ownerMemberId === holderId && c.currentBalanceCents > 0),
    [tarjetas, holderId],
  )

  if (isPending) return <Cargando />

  return (
    <>
      {deudas.length > 0 ? (
        <>
          <Encabezado>Pendiente en tarjetas</Encabezado>
          {deudas.map((c) => (
            <FilaDetalle
              key={c.id}
              titulo={c.name}
              detalle={c.last4 ? `•••• ${c.last4}` : undefined}
              monto={<Money cents={c.currentBalanceCents ?? 0} tone="expense" />}
              onClick={() => {
                onCerrar()
                navigate('/tarjetas')
              }}
            />
          ))}
        </>
      ) : null}

      <Encabezado>Movimientos del periodo</Encabezado>
      {movimientos.length === 0 ? (
        <Vacio texto="No hay gastos registrados en este periodo." />
      ) : (
        movimientos.map((t) => (
          <FilaDetalle
            key={t.id}
            titulo={t.description || t.category?.name || 'Gasto'}
            detalle={`${t.occurredOn} · ${comoSePago(t)}`}
            monto={<Money cents={t.amountCents} tone="expense" />}
            onClick={() => {
              onCerrar()
              navigate('/movimientos')
            }}
          />
        ))
      )}
    </>
  )
}

/** Con qué se pagó, en una línea: es la mitad de la pregunta de la espec §20. */
function comoSePago(t: Transaction): string {
  if (t.paymentSource === 'CREDIT_CARD') {
    if (!t.creditCard) return 'Crédito'
    return t.creditCard.last4 ? `${t.creditCard.name} •••• ${t.creditCard.last4}` : t.creditCard.name
  }
  return t.account?.name ?? 'Efectivo'
}

function nombreTipo(tipo: string): string {
  const nombres: Record<string, string> = {
    CHECKING: 'Cuenta de cheques',
    CASH: 'Efectivo',
    SAVINGS: 'Ahorro',
    MEAL_VOUCHER: 'Vales de despensa',
    INVESTMENT: 'Inversión',
  }
  return nombres[tipo] ?? 'Cuenta'
}

function formatoCorto(cents: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

function Encabezado({ children }: { children: string }) {
  return (
    <h3 className="mt-2 px-1 text-[12px] font-semibold uppercase tracking-[0.07em] text-ink-faint">
      {children}
    </h3>
  )
}

function Cargando() {
  return (
    <div className="flex flex-col gap-2" aria-busy>
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-[52px] animate-pulse rounded-card bg-surface-sunken" />
      ))}
    </div>
  )
}

function Vacio({ texto }: { texto: string }) {
  return (
    <p className="rounded-card bg-surface px-3.5 py-6 text-center text-[13px] text-ink-soft">
      {texto}
    </p>
  )
}
